import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { userRequest } from "../utils/utils";
import {
  IAddBalanceRequest,
  IBalance,
  IExchangeRate,
  IPaymentInfoResponse,
  IPaymentOrderRequest,
  IPaymentOrderResponse,
  ITransactions,
} from "../types";

interface IUserState {
  partnerBalance?: IBalance | null;
  transactions: ITransactions | null;
  message: string;
  exchangeRate: IExchangeRate | null;
  payment_url: string | null;
  payment_link: IPaymentInfoResponse | null;
}

const initialState: IUserState = {
  partnerBalance: null,
  transactions: null,
  message: "",
  exchangeRate: null,
  payment_url: null,
  payment_link: null,
};

export const getPartnerBalance = createAsyncThunk<
  IBalance,
  void,
  { rejectValue: { error: string } }
>("billing/getPartnerBalance", async (_, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`billing/balances/`, {});
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getExchangeRate = createAsyncThunk<
  IExchangeRate,
  void,
  { rejectValue: { error: string } }
>("billing/getExchangeRate", async (_, { getState, rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`billing/exchange-rate/`, {});
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || error.messages.message,
    });
  }
});

export const getPaymentInfo = createAsyncThunk<
  IPaymentInfoResponse,
  string,
  {
    rejectValue: { error: string };
  }
>("billing/getPaymentInfo", async (id, { getState, rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`billing/search/${id}`, {});
    return data;
  } catch (error: any) {
    console.error(error);

    return rejectWithValue({
      error: error.response.data.message || error.message,
    });
  }
});

export const getPaymentLink = createAsyncThunk<
  IPaymentInfoResponse,
  string,
  {
    rejectValue: { error: string };
  }
>("billing/getPaymentLink", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`billing/app/lnk/${id}/`, {});
    return data;
  } catch (error: any) {
    console.error(error);

    return rejectWithValue({
      error: error.response.data.message || error.message,
    });
  }
});

export const createPaymentOrder = createAsyncThunk<
  IPaymentOrderResponse,
  IPaymentOrderRequest | { rejectValue: { error: string } }
>(
  "billing/createPaymentOrder",
  async (params, { getState, rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(`billing/orders/card/`, params);
      return data;
    } catch (error: any) {
      console.log("err", error);

      return rejectWithValue({
        error: error.message || error.messages.message | error,
      });
    }
  }
);

export const getTransactionsHistory = createAsyncThunk<
  ITransactions,
  | {
      start_date?: string | undefined;
      end_date?: string | undefined;
      user_id?: number;
    }
  | undefined,
  { rejectValue: { error: string } }
>(
  "billing/getTransactionsHistory",
  async (params, { getState, rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `billing/search/N250305623392/`,
        params
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const addPartnerBalance = createAsyncThunk<
  { detail: string; id: number },
  IAddBalanceRequest
>(
  "billing/addPartnerBalance",
  async (params, { getState, rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/manage/api/add_balance/`,
        params
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const reducePartnerBalance = createAsyncThunk<
  { detail: string; id: number },
  IAddBalanceRequest
>(
  "billing/reducePartnerBalance",
  async (params, { getState, rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/manage/api/deduct_balance/`,
        params
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || error.messages.message,
      });
    }
  }
);

export const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    resetBilling: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getPartnerBalance.fulfilled, (state, action) => {
      state.partnerBalance = action.payload;
    });
    builder.addCase(getTransactionsHistory.fulfilled, (state, action) => {
      state.transactions = action.payload;
    });
    builder.addCase(addPartnerBalance.fulfilled, (state, action) => {
      state.message = action.payload.detail;
    });
    builder.addCase(reducePartnerBalance.fulfilled, (state, action) => {
      state.message = action.payload.detail;
    });
    builder.addCase(getExchangeRate.fulfilled, (state, action) => {
      state.exchangeRate = action.payload;
    });
    builder.addCase(createPaymentOrder.fulfilled, (state, action) => {
      state.payment_url = action.payload.payment_url;
    });
    builder.addCase(getPaymentLink.fulfilled, (state, action) => {
      state.payment_link = action.payload;
    });
  },
});

export const { resetBilling } = billingSlice.actions;

export default billingSlice.reducer;
